import type { CSSProperties, ElementType, PropsWithChildren } from 'react';

import classNames from 'classnames';

import type { PolymorphicPropsWithRef } from '@/shared/types';
import type { OpdsStyleProps } from '@/shared/ui/Box';
import { Box } from '@/shared/ui/Box';

import styles from './Flex.module.css';

export type FlexAlign = 'stretch' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'start' | 'end';
export type FlexJustify =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | 'start'
  | 'end';
export type FlexWrap = 'wrap' | 'nowrap' | 'wrap-reverse';
export type FlexDirection = 'column' | 'column-reverse' | 'row' | 'row-reverse';

export type FlexProps = OpdsStyleProps & {
  inline?: boolean;
  gap?: number | string;
  rowGap?: number | string;
  columnGap?: number | string;
  flex?: React.CSSProperties['flex'];
  align?: FlexAlign;
  justify?: FlexJustify;
  wrap?: FlexWrap;
  direction?: FlexDirection;
};

type PolymorphicFlexProps<C extends ElementType> = PropsWithChildren & PolymorphicPropsWithRef<C, FlexProps>;

const getFlexClasses = (
  inline?: boolean,
  align?: FlexAlign,
  justify?: FlexJustify,
  wrap?: FlexWrap,
  direction?: FlexDirection
) => {
  const classes: string[] = [inline ? styles.inlineFlex : styles.flex];

  if (align) classes.push(styles[`align-${align}`]);
  if (justify) classes.push(styles[`justify-${justify}`]);
  if (wrap) classes.push(styles[`wrap-${wrap}`]);
  if (direction) classes.push(styles[`direction-${direction}`]);

  return classes;
};

const getComputedFlexStyle = (
  style?: CSSProperties,
  gap?: number | string,
  rowGap?: number | string,
  columnGap?: number | string,
  flex?: React.CSSProperties['flex']
) => {
  const computedStyle: CSSProperties = { ...style };

  if (gap !== undefined) computedStyle.gap = gap;
  if (rowGap !== undefined) computedStyle.rowGap = rowGap;
  if (columnGap !== undefined) computedStyle.columnGap = columnGap;
  if (flex !== undefined) computedStyle.flex = flex;

  return computedStyle;
};

const Flex = <C extends ElementType = 'div'>(props: PolymorphicFlexProps<C>) => {
  const {
    inline = false,
    className,
    gap,
    rowGap,
    columnGap,
    children,
    style,
    align,
    as,
    justify,
    wrap,
    direction,
    flex,
    ...rest
  } = props;

  const classes = getFlexClasses(inline, align, justify, wrap, direction);
  const computedStyle = getComputedFlexStyle(style, gap, rowGap, columnGap, flex);

  return (
    <Box className={classNames(classes, className)} as={as as ElementType} style={computedStyle} {...rest}>
      {children}
    </Box>
  );
};

export default Flex;
